import { useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { api } from '@/lib/api';

/**
 * Corregir la serie o el número de la factura que el CRM emite en nombre del
 * propietario (explotación). Es el arreglo de urgencia cuando sale con un
 * número desfasado: rehacer la liquidación perdería los gastos ya marcados
 * como cobrados, y así solo cambia el número y el PDF se regenera.
 */
export default function CambiarNumeroFactura({
  liquidacion,
  owner,
  esLaUltima,
  onGuardado,
  onCerrar,
}: {
  liquidacion: any;
  owner: any;
  /** Es la liquidación más reciente del propietario: la siguiente debería seguirla */
  esLaUltima: boolean;
  onGuardado: (r: { liquidacion: any; owner: any | null }) => void;
  onCerrar: () => void;
}) {
  const [prefix, setPrefix] = useState<string>(liquidacion.invoicePrefix ?? owner?.invoicePrefix ?? '');
  const [num, setNum] = useState<string>(
    liquidacion.invoiceNum != null ? String(liquidacion.invoiceNum) : owner?.invoiceNextNum != null ? String(owner.invoiceNextNum) : '',
  );
  const [actualizarSiguiente, setActualizarSiguiente] = useState(esLaUltima);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');

  const actual = liquidacion.invoiceNum != null ? `${liquidacion.invoicePrefix ?? ''}${liquidacion.invoiceNum}` : null;
  const nuevo = `${prefix.trim()}${num.trim()}`;
  const n = Number(num);
  const valido = prefix.trim() !== '' && !/\s/.test(prefix.trim()) && Number.isInteger(n) && n > 0;
  const siguienteAhora = owner?.invoiceNextNum != null ? `${owner.invoicePrefix ?? ''}${owner.invoiceNextNum}` : null;

  async function guardar() {
    setGuardando(true);
    setError('');
    try {
      const r = await api.cambiarNumeroFactura(liquidacion.id, { prefix: prefix.trim(), num: n, actualizarSiguiente });
      onGuardado(r);
    } catch (err: any) {
      setError(err?.message || 'No se pudo cambiar el número');
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 shadow-xl max-w-md w-full space-y-4">
        <div>
          <h3 className="text-lg font-bold text-sereno-dark">Cambiar número de factura</h3>
          <p className="text-sm text-sereno-dark/60 mt-1">
            La factura se regenera con el número nuevo. La liquidación no se rehace: los gastos cobrados se quedan como están.
          </p>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <label className="block text-xs font-semibold text-sereno-dark/60 uppercase tracking-wide mb-1.5">Serie</label>
            <input className="input font-mono" value={prefix} onChange={e => setPrefix(e.target.value)} placeholder="SERENOLUCERO-2026-" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-sereno-dark/60 uppercase tracking-wide mb-1.5">Número</label>
            <input className="input font-mono" inputMode="numeric" value={num} onChange={e => setNum(e.target.value.replace(/[^\d]/g, ''))} />
          </div>
        </div>

        <div className="rounded-lg bg-gray-50 border border-gray-200 p-3 text-sm space-y-1">
          {actual && <p className="text-sereno-dark/60">Ahora: <span className="font-mono">{actual}</span></p>}
          <p className="text-sereno-dark">Quedará: <span className="font-mono font-semibold">{valido ? nuevo : '—'}</span></p>
        </div>

        <label className="flex items-start gap-2 text-sm text-sereno-dark">
          <input type="checkbox" className="mt-0.5" checked={actualizarSiguiente} onChange={e => setActualizarSiguiente(e.target.checked)} />
          <span>
            Que la siguiente factura de este propietario sea la <span className="font-mono">{valido ? `${prefix.trim()}${n + 1}` : '—'}</span>
            {siguienteAhora && <span className="text-sereno-dark/50"> (ahora sería la <span className="font-mono">{siguienteAhora}</span>)</span>}
          </span>
        </label>

        {error && (
          <div className="flex items-start gap-2 text-sm rounded-lg border p-3 bg-red-50 border-red-200 text-red-700">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" /> <span>{error}</span>
          </div>
        )}

        <div className="flex gap-3 pt-1">
          <button onClick={onCerrar} disabled={guardando} className="btn-ghost flex-1">Cancelar</button>
          <button onClick={guardar} disabled={!valido || guardando || nuevo === actual && !actualizarSiguiente} className="btn-primary flex-1 flex items-center justify-center gap-2">
            {guardando && <Loader2 className="animate-spin" size={16} />}
            Guardar y regenerar
          </button>
        </div>
      </div>
    </div>
  );
}
